import React, { useRef } from "react";
import { Link } from "react-router-dom";
import {
  BellIcon,
  SearchIcon,
  ChevRightIcon,
  MenuIcon,
  PanelLeftIcon,
} from "./ShellIcons";
import { ReadinessChip } from "../../ds";
import { TOPBAR_SEARCH_WIDTH } from "../topbarDropdownStyles";

interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface TopbarProps {
  breadcrumbs?: BreadcrumbItem[];
  hasNotification?: boolean;
  onNotificationClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onSearchClick?: () => void;
  /** Mobile: show hamburger to open mobile nav drawer */
  onMenuClick?: () => void;
  isMobile?: boolean;
  /** Tablet: show panel-left toggle to open subnav overlay */
  isTablet?: boolean;
  /** Whether the tablet subnav overlay is currently open */
  subNavOpen?: boolean;
  /** Tablet: callback to toggle the subnav overlay */
  onSubNavToggle?: () => void;
  /** Readiness 0–100. Pass undefined to hide the chip while loading. */
  readiness?: number;
  /** Slot rendered between breadcrumbs and the search bar (e.g. tenant/AM dropdown). */
  tenantSlot?: React.ReactNode;
  children?: React.ReactNode;
}

const Topbar: React.FC<TopbarProps> = ({
  breadcrumbs = [],
  hasNotification = false,
  onNotificationClick,
  onSearchClick,
  onMenuClick,
  isMobile = false,
  isTablet = false,
  subNavOpen = false,
  onSubNavToggle,
  readiness,
  tenantSlot,
  children,
}) => {
  const notifBtnRef = useRef<HTMLButtonElement>(null);

  const handleNotifClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onNotificationClick?.(e);
  };

  return (
    <header
      role="banner"
      style={{
        gridArea: "topbar",
        background: "var(--bg)",
        borderBottom: "1px solid var(--line)",
        display: "flex",
        alignItems: "center",
        padding: "0 16px",
        gap: 12,
        height: "var(--topbar-h)",
        zIndex: 20,
      }}
    >
      {/* Mobile hamburger */}
      {isMobile && (
        <button
          type="button"
          onClick={onMenuClick}
          aria-label="Open navigation menu"
          style={{
            width: 36,
            height: 36,
            borderRadius: "var(--r-sm)",
            border: "none",
            background: "transparent",
            color: "var(--ink-2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <MenuIcon size={18} />
        </button>
      )}

      {/* Tablet subnav toggle */}
      {isTablet && onSubNavToggle && (
        <button
          type="button"
          onClick={onSubNavToggle}
          aria-label={subNavOpen ? "Close section menu" : "Open section menu"}
          aria-expanded={subNavOpen}
          style={{
            width: 36,
            height: 36,
            borderRadius: "var(--r-sm)",
            border: "none",
            background: subNavOpen ? "var(--bg-elev)" : "transparent",
            color: subNavOpen ? "var(--ink-1)" : "var(--ink-2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            flexShrink: 0,
            transition: "background 100ms, color 100ms",
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background =
              "var(--bg-elev)";
            (e.currentTarget as HTMLButtonElement).style.color = "var(--ink-1)";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = subNavOpen
              ? "var(--bg-elev)"
              : "transparent";
            (e.currentTarget as HTMLButtonElement).style.color = subNavOpen
              ? "var(--ink-1)"
              : "var(--ink-2)";
          }}
        >
          <PanelLeftIcon size={16} />
        </button>
      )}

      {/* Breadcrumbs — on mobile show only the current section (last crumb) */}
      <nav
        aria-label="Page location"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        {breadcrumbs.length > 0 ? (
          isMobile ? (
            /* Mobile: show only the last breadcrumb — keeps topbar uncluttered */
            <span
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: "var(--ink-1)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {breadcrumbs[breadcrumbs.length - 1].label}
            </span>
          ) : (
            /* Desktop: full breadcrumb trail with chevrons */
            breadcrumbs.map((b, i) => {
              const isLast = i === breadcrumbs.length - 1;
              const baseStyle: React.CSSProperties = {
                fontSize: 13,
                color: isLast ? "var(--ink-1)" : "var(--ink-3)",
                fontWeight: isLast ? 500 : 400,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                textDecoration: "none",
              };
              return (
                <React.Fragment key={i}>
                  {b.path && !isLast ? (
                    <Link
                      to={b.path}
                      style={baseStyle}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLAnchorElement).style.color =
                          "var(--ink-1)";
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLAnchorElement).style.color =
                          "var(--ink-3)";
                      }}
                    >
                      {b.label}
                    </Link>
                  ) : (
                    <span style={baseStyle}>{b.label}</span>
                  )}
                  {!isLast && (
                    <span
                      aria-hidden="true"
                      style={{
                        color: "var(--ink-4)",
                        display: "flex",
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <ChevRightIcon size={10} />
                    </span>
                  )}
                </React.Fragment>
              );
            })
          )
        ) : (
          <span
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: "var(--ink-1)",
            }}
          >
            Alpha Health
          </span>
        )}
      </nav>

      {/* Tenant / AM customer dropdown slot — same min-width rule as search bar */}
      {tenantSlot && (
        <div
          style={{
            minWidth: isMobile
              ? "auto"
              : isTablet
                ? TOPBAR_SEARCH_WIDTH.tablet
                : TOPBAR_SEARCH_WIDTH.desktop,
            flexShrink: 0,
          }}
        >
          {tenantSlot}
        </div>
      )}

      {/* Search trigger (Cmd+K) */}
      <button
        type="button"
        onClick={onSearchClick}
        aria-label="Search (Cmd+K)"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          height: 32,
          padding: "0 10px",
          borderRadius: "var(--r-sm)",
          background: "var(--bg-elev)",
          border: "1px solid var(--line)",
          color: "var(--ink-3)",
          fontSize: 13,
          minWidth: isMobile
            ? "auto"
            : isTablet
              ? TOPBAR_SEARCH_WIDTH.tablet
              : TOPBAR_SEARCH_WIDTH.desktop,
          cursor: "pointer",
          flexShrink: 0,
          transition: "border-color 100ms",
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.borderColor =
            "var(--line-strong)";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.borderColor =
            "var(--line)";
        }}
      >
        <SearchIcon size={14} />
        {!isMobile && (
          <span style={{ flex: 1, textAlign: "left" }}>
            Search rules, cases, docs…
          </span>
        )}
        <span
          aria-hidden="true"
          style={{
            fontSize: 10,
            fontFamily: "var(--font-mono)",
            background: "var(--bg-sunken)",
            border: "1px solid var(--line)",
            borderRadius: 4,
            padding: "1px 5px",
            color: "var(--ink-3)",
          }}
        >
          ⌘K
        </span>
      </button>

      {/* Readiness widget — desktop only, only when score is available */}
      {!isMobile && typeof readiness === "number" && (
        <ReadinessChip value={readiness} />
      )}

      {/* Right-side actions */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          flexShrink: 0,
        }}
      >
        {/* Notification bell */}
        <button
          ref={notifBtnRef}
          type="button"
          onClick={handleNotifClick}
          aria-label="View notifications"
          style={{
            position: "relative",
            width: 36,
            height: 36,
            borderRadius: "var(--r-sm)",
            border: "none",
            background: "transparent",
            color: "var(--ink-2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "background 100ms, color 100ms",
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background =
              "var(--bg-elev)";
            (e.currentTarget as HTMLButtonElement).style.color = "var(--ink-1)";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background =
              "transparent";
            (e.currentTarget as HTMLButtonElement).style.color = "var(--ink-2)";
          }}
        >
          <BellIcon size={16} />
          {hasNotification && (
            <span
              aria-label="New notifications"
              style={{
                position: "absolute",
                top: 7,
                right: 7,
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "var(--risk)",
                border: "1.5px solid var(--bg)",
              }}
            />
          )}
        </button>

        {/* Slot for additional items (user menu, etc.) */}
        {children}
      </div>
    </header>
  );
};

export default Topbar;
